import { useCallback, useEffect, useState } from "react";
import * as api from "../../api/client";
import type { AdminJob } from "../../api/client";
import { Banner } from "./forms";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";
import { simplePanelStyle } from "../../theme/simplePanel";

/**
 * Admin "Image Creator" tool. Describe an image, pick a size, and the
 * PixelLab v2 endpoint returns a grid of variations (the count is set
 * by the size). Each variation is snapped to Endesga-32 server-side;
 * this view shows them as a grid of individual images you can download.
 *
 * Each run is one `generate_image_grid` admin job, so the run list is
 * just the job list filtered to that kind — persisted across reloads.
 */

// Square sizes the form offers, each tagged with how many variations
// PixelLab returns at that size (max dimension decides the grid):
// <=42 -> 64 (8x8), 43-85 -> 16 (4x4), 86-170 -> 4 (2x2), >170 -> 1.
const SIZE_PRESETS: ReadonlyArray<{ size: number; count: number }> = [
  { size: 32, count: 64 },
  { size: 64, count: 16 },
  { size: 128, count: 4 },
  { size: 256, count: 1 },
];

const DEFAULT_SIZE = 64;

export function ImageCreatorTool() {
  const theme = pixelTheme;

  const [description, setDescription] = useState("");
  const [size, setSize] = useState<number>(DEFAULT_SIZE);
  // The endpoint removes the background by default; we default the tool
  // to KEEPING it, exposing removal as an opt-in toggle.
  const [removeBackground, setRemoveBackground] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<AdminJob[]>([]);

  const refreshRuns = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      setRuns(jobs.filter((j) => j.kind === "generate_image_grid"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
    // Poll so a freshly-finished run (and its images) appears without a
    // manual refresh while generation is in flight.
    const id = setInterval(() => void refreshRuns(), 3000);
    return () => clearInterval(id);
  }, [refreshRuns]);

  const onGenerate = useCallback(async () => {
    setError(null);
    if (description.trim() === "") {
      setError("Enter a description.");
      return;
    }
    setBusy(true);
    try {
      await api.enqueueImageCreator({
        description: description.trim(),
        size,
        no_background: removeBackground,
      });
      await refreshRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [description, size, removeBackground, refreshRuns]);

  const activePreset = SIZE_PRESETS.find((p) => p.size === size);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          ...simplePanelStyle(theme),
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          IMAGE CREATOR
        </div>
        <div style={{ fontSize: 12, color: theme.inkFaint, lineHeight: 1.4 }}>
          Describe an image and pick a size. PixelLab returns a grid of variations (size
          decides how many), each snapped to Endesga-32. Download whichever you want from a
          finished run below.
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabelStyle(theme)}>DESCRIPTION</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="a mossy stone idol, glowing runes"
            rows={2}
            style={{
              padding: "6px 8px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `1px solid ${theme.panelEdge}`,
              fontFamily: "monospace",
              fontSize: 13,
              resize: "vertical",
            }}
          />
        </label>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabelStyle(theme)}>SIZE</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SIZE_PRESETS.map((p) => {
              const active = p.size === size;
              return (
                <button
                  key={p.size}
                  type="button"
                  onClick={() => setSize(p.size)}
                  style={{
                    ...pillButtonStyle(theme, false),
                    background: active ? theme.accent : theme.panel,
                    color: active ? E32.parchment : theme.ink,
                    border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
                  }}
                >
                  {p.size}×{p.size} · {p.count} IMG{p.count === 1 ? "" : "S"}
                </button>
              );
            })}
          </div>
        </div>

        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={removeBackground}
            onChange={(e) => setRemoveBackground(e.target.checked)}
          />
          <span style={{ color: theme.ink, fontSize: 13 }}>
            Remove background (transparent)
          </span>
        </label>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => void onGenerate()}
            disabled={busy}
            style={pillButtonStyle(theme, busy)}
          >
            {busy
              ? "ENQUEUING…"
              : `GENERATE ${activePreset ? activePreset.count : ""} IMAGE${
                  activePreset && activePreset.count === 1 ? "" : "S"
                }`}
          </button>
          <span style={{ color: theme.inkFaint, fontSize: 12 }}>
            Generation runs in the background; the run appears below.
          </span>
        </div>
      </div>

      {error && (
        <Banner theme={theme} kind="error">
          {error}
        </Banner>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={fieldLabelStyle(theme)}>RUNS</div>
        {runs.length === 0 ? (
          <div style={{ color: theme.inkFaint, fontSize: 13 }}>No runs yet.</div>
        ) : (
          runs.map((run) => <RunCard key={run.id} run={run} theme={theme} />)
        )}
      </div>
    </div>
  );
}

function RunCard({ run, theme }: { run: AdminJob; theme: typeof pixelTheme }) {
  const description = String(run.args?.description ?? "—");
  const size = typeof run.args?.size === "number" ? run.args.size : undefined;
  const noBackground = run.args?.no_background === true;
  const count = run.result?.count ?? 0;

  return (
    <div
      style={{
        ...simplePanelStyle(theme),
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: theme.ink, fontSize: 13, fontFamily: "monospace" }}>
          {description}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
          {size != null && (
            <span style={{ color: theme.inkFaint, fontSize: 11 }}>
              {size}×{size}
              {noBackground ? " · no bg" : ""}
            </span>
          )}
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: jobStatusColor(theme, run.status),
            }}
          >
            {run.status.toUpperCase()}
          </span>
        </div>
      </div>

      {run.status === "failed" && (
        <div
          style={{
            padding: 8,
            background: theme.bgDeep,
            border: `1px solid ${theme.accent}`,
            color: theme.accent,
            fontSize: 12,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {run.error ?? "generation failed"}
        </div>
      )}

      {(run.status === "queued" || run.status === "running") && (
        <div style={{ color: theme.inkFaint, fontSize: 12 }}>
          {run.status === "queued" ? "Waiting to start…" : "Generating…"}
        </div>
      )}

      {run.status === "succeeded" && count > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
            gap: 8,
          }}
        >
          {Array.from({ length: count }, (_, i) => (
            <ImageCell
              key={i}
              url={api.imageCreatorImageUrl(run.id, i)}
              downloadName={downloadName(description, size, i)}
              theme={theme}
            />
          ))}
        </div>
      )}

      {run.status === "succeeded" && count === 0 && (
        <div style={{ color: theme.inkFaint, fontSize: 12 }}>
          Run finished but produced no images.
        </div>
      )}
    </div>
  );
}

function ImageCell({
  url,
  downloadName,
  theme,
}: {
  url: string;
  downloadName: string;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        position: "relative",
        background: E32.parchment,
        border: `2px solid ${theme.panelEdge}`,
        aspectRatio: "1 / 1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <img
        src={url}
        alt="generated variation"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          imageRendering: "pixelated",
        }}
      />
      <a
        href={url}
        download={downloadName}
        title="Download"
        style={{
          position: "absolute",
          right: 4,
          bottom: 4,
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          letterSpacing: theme.letterSpacing,
          padding: "3px 6px",
          background: theme.accent,
          color: E32.parchment,
          border: `1px solid ${theme.accent}`,
          textDecoration: "none",
        }}
      >
        ↓
      </a>
    </div>
  );
}

// A friendly download filename: a slug of the prompt, the size, and the
// 0-based index, e.g. `mossy-stone-idol-64-03.png`.
function downloadName(description: string, size: number | undefined, idx: number): string {
  const slug =
    description
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "image";
  const idxPart = String(idx).padStart(2, "0");
  return size != null ? `${slug}-${size}-${idxPart}.png` : `${slug}-${idxPart}.png`;
}

function jobStatusColor(theme: typeof pixelTheme, status: AdminJob["status"]): string {
  switch (status) {
    case "queued":
      return theme.inkFaint;
    case "running":
      return theme.accent;
    case "succeeded":
      return theme.ink;
    case "failed":
      return theme.accent;
  }
}

function fieldLabelStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 9,
    letterSpacing: theme.letterSpacing,
    color: theme.inkDim,
  };
}

function pillButtonStyle(
  theme: typeof pixelTheme,
  disabled: boolean,
): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    padding: "6px 10px",
    background: disabled ? theme.panel : theme.accent,
    color: disabled ? theme.inkFaint : E32.parchment,
    border: `2px solid ${disabled ? theme.panelEdge : theme.accent}`,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
